import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuSmile, LuCamera, LuImage } from 'react-icons/lu';
import TipChip from './TipChip';

// Mobile photo-picker bottom sheet (scan flow step 2). Tapping CAPTURE on the
// intro opens this; it hosts the posing guidance and a single CHOOSE PHOTO
// action that fires the native file picker (camera or library). Selecting a
// file unmounts this and hands off to the fullscreen processing overlay.
//
// Follows the AnimatePresence + backdrop + spring pattern from ScanRevealModal,
// anchored to the bottom of the viewport (rounded-t-card) like a native sheet.
//
// Props:
//   open        boolean
//   onChoose    () => void   — invokes the dropzone open()
//   onClose     () => void   — backdrop / Cancel
//   inputProps  dropzone getInputProps() result (mounted here so open() works)
const ScanPhotoPickerModal = ({ open, onChoose, onClose, inputProps }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="lg:hidden fixed inset-0 z-[80] flex items-end justify-center"
        style={{ background: 'rgba(0,0,0,0.72)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.24 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Select a portrait"
      >
        {inputProps && <input {...inputProps} aria-label="portrait upload" />}
        <motion.div
          className="relative w-full rounded-t-card px-5 pt-7 pb-[calc(28px+env(safe-area-inset-bottom))] text-center overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1B1B1A, #0f0f0e)',
            borderTop: '1px solid rgba(201,164,92,0.28)',
            boxShadow: '0 -24px 60px -20px rgba(0,0,0,0.9)',
          }}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            aria-hidden="true"
            className="mx-auto mb-6 h-1 w-10 rounded-full bg-[rgba(255,255,255,0.18)]"
          />
          <div className="font-display uppercase text-xs tracking-[0.28em] text-aurum">
            SUBMIT PORTRAIT FOR ANALYSIS
          </div>
          <p className="font-display italic text-[15px] text-bone-dim mt-2.5 mb-6 m-0">
            Frame your face cleanly before you capture.
          </p>

          <div className="flex flex-col gap-2.5">
            <TipChip
              icon={<LuSmile size={16} strokeWidth={1.5} />}
              label="Neutral expression, even lighting"
            />
            <TipChip
              icon={<LuCamera size={16} strokeWidth={1.5} />}
              label="Front camera, arm’s length"
            />
          </div>

          <button
            type="button"
            onClick={onChoose}
            className="w-full h-[54px] rounded-field border-0 cursor-pointer flex items-center justify-center gap-3 font-display font-semibold text-[13px] uppercase tracking-[0.22em] text-[#1A1A1A] mt-6"
            style={{ background: 'linear-gradient(180deg, #F0ECE2, #C2BBAC)' }}
          >
            <LuImage size={18} strokeWidth={1.6} aria-hidden="true" />
            <span>CHOOSE PHOTO</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="mt-3.5 bg-transparent border-0 cursor-pointer font-display uppercase text-[12px] tracking-[0.22em] text-bone-dim hover:text-bone transition-colors duration-200"
          >
            CANCEL
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default ScanPhotoPickerModal;
